import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import {
  crearTurnoDePrueba, getMyShifts, getMyShiftsRange, getNextShift, type MyShiftRow,
} from '../../api/assignments';
import { dinamicasDeLasCampanias, type DinamicasDeCampania } from '../../api/impulso';
import { rotulosVisitasPuesto } from '../../api/softys';
import { ROTULOS_DE_SIEMPRE, type RotulosVisitasPuesto } from '../../domain/softysConfig';
import { listMyAttendance } from '../../api/attendance';
import {
  discardPending,
  listFailed,
  listPending,
  pendingCount,
  type QueuedAttendance,
} from '../../offline/queue';
import { esErrorDeVersionVieja } from '../../offline/db';
import { computeShiftState, type ShiftMark } from '../../lib/shiftState';
import { useAuth } from '../../components/AuthProvider';

const LIMA_TZ = 'America/Lima';

/** Fecha de hoy en Lima (YYYY-MM-DD). */
export function todayInLima(): string {
  return formatInTimeZone(new Date(), LIMA_TZ, 'yyyy-MM-dd');
}

const hhmm = (t: string): string => t.slice(0, 5);
const ddmmyyyy = (d: string): string => formatInTimeZone(`${d}T12:00:00Z`, LIMA_TZ, 'dd/MM/yyyy');
const fechaHora = (ts: string): string =>
  formatInTimeZone(new Date(ts), LIMA_TZ, 'dd/MM/yyyy HH:mm');

/** Una tarjeta por turno: sus propios datos, su propio estado y sus propios botones.
 *  El recurso puede tener dos turnos el mismo día en PDV distintos. */
function TarjetaTurno({
  shift,
  marks,
  indice,
  total,
  onMarcar,
  marcasSinLeer,
}: {
  shift: MyShiftRow;
  marks: ShiftMark[];
  indice: number;
  total: number;
  onMarcar: (assignmentId: string, kind: 'ingreso' | 'salida') => void;
  /** No se pudo leer lo que ya está en el servidor: `marks` trae solo lo de la cola local. */
  marcasSinLeer: boolean;
}) {
  const state = computeShiftState(marks);
  const label = marcasSinLeer && !state.hasIngreso && !state.hasSalida
    ? 'No se pudieron leer tus marcas'
    : state.label;

  return (
    <section className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      {total > 1 && (
        <p className="text-xs font-bold uppercase tracking-wide text-blue-700">
          Turno {indice + 1} de {total}
        </p>
      )}
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500">
          {shift.position.campaign.client}
        </p>
        <h2 className="text-xl font-bold text-gray-900">{shift.position.campaign.brand}</h2>

        <p className="mt-3 text-base font-medium text-gray-900">{shift.position.pdv.name}</p>
        <p className="text-sm text-gray-600">
          {shift.position.pdv.address ?? 'Sin dirección registrada'}
          {shift.position.pdv.district ? ` — ${shift.position.pdv.district}` : ''}
        </p>

        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div>
            <dt className="text-gray-500">Fecha</dt>
            <dd className="font-medium text-gray-900">{ddmmyyyy(shift.position.date)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Horario</dt>
            <dd className="font-medium text-gray-900">
              {hhmm(shift.position.start_time)} a {hhmm(shift.position.end_time)}
            </dd>
          </div>
        </dl>

        <p className="mt-3 inline-block rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-800">
          {label}
        </p>
      </div>

      <button
        onClick={() => onMarcar(shift.id, 'ingreso')}
        disabled={state.hasIngreso}
        className="w-full rounded-xl bg-blue-600 py-5 text-lg font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
      >
        Marcar ingreso
      </button>

      <button
        onClick={() => onMarcar(shift.id, 'salida')}
        // Sin poder leer las marcas, no se sabe si hubo ingreso: la salida NO se apaga por eso. El
        // servidor la acepta sin ingreso y un marcado repetido no se duplica (`submitOrQueue` lo
        // toma como enviado). Apagarla fue el bucle del 18-oct: «Sin marcar», volver a marcar el
        // ingreso, «ya existe», y la salida sin aparecer nunca.
        disabled={marcasSinLeer ? state.hasSalida : !state.hasIngreso || state.hasSalida}
        className="w-full rounded-xl bg-green-600 py-5 text-lg font-bold text-white disabled:bg-gray-300 disabled:text-gray-500"
      >
        Marcar salida
      </button>
    </section>
  );
}

/**
 * Registrar canjes, al MISMO nivel visual que marcar y **fuera de la tarjeta del turno**.
 *
 * Estuvo dentro de la tarjeta, y eso lo ataba a tener turno hoy. En las tiendas no dejan usar el
 * celular ni tomar fotos, así que las impulsadoras registran al terminar la jornada: de noche, y a
 * veces pasada la medianoche. A la 1 a.m. el día en Lima ya es otro, la tarjeta del turno no se
 * pinta y desaparecía el único camino a la pantalla de canjes — con el trabajo de todo el día sin
 * poder subirse.
 *
 * Va en otro color porque no es un marcado: no sella hora ni asistencia. Y va una sola vez por
 * pantalla, no por turno: la pantalla de canjes ya elige día y turno por su cuenta, así que dos
 * botones idénticos llevando al mismo sitio solo eran ruido.
 */
function BotonCanjes({ fueraDeTurno }: { fueraDeTurno: boolean }) {
  return (
    <Link
      to="/movil/canjes"
      className="block w-full rounded-xl bg-indigo-600 py-5 text-center text-lg font-bold text-white"
    >
      {/* El rótulo ES el aviso. Se probó con un párrafo explicativo debajo y el usuario lo
          descartó: "no sé si la pregunta sea necesaria […] que diga registrar canje fuera de
          turno nomás, pero para que sepa". Tiene razón — quien entra de noche a subir lo del día
          ya sabe lo que viene a hacer; lo único que necesita es confirmar que está en el sitio
          correcto, y para eso alcanza el nombre del botón. */}
      {fueraDeTurno ? 'Registrar canje fuera de turno' : 'Registrar canje'}
    </Link>
  );
}

/**
 * Cierre de turno — TERCER paso del circuito, después de marcar ingreso y salida.
 *
 * Va debajo de los dos marcados y en el mismo lugar que canjes —fuera de la tarjeta del turno—
 * por el mismo motivo: se llena al terminar la jornada, de noche, y a veces pasada la medianoche,
 * cuando la tarjeta del día ya no se pinta.
 *
 * El orden importa y lo fijó el usuario: *«la prioridad es que marquen su asistencia, ingreso,
 * salida, y después el formulario viene»*. Por eso este botón es el último y en otro color: no es
 * un marcado, no sella hora, y perderlo no le cuesta el día a nadie.
 */
function BotonCierreDeTurno() {
  return (
    <Link
      to="/movil/cierre-de-turno"
      className="block w-full rounded-xl bg-slate-700 py-5 text-center text-lg font-bold text-white"
    >
      Registrar cierre de turno
    </Link>
  );
}

/**
 * Los dos botones de Softys · Toallitas Húmedas: visita a comerciante y visita de venta.
 *
 * Van JUNTOS y los dos encendidos a la vez, sin orden entre ellos. Decisión del jefe de
 * operaciones (13-set): la impulsadora recorre el mercado puesto por puesto y alterna los dos
 * tipos según lo que se dé en cada uno; forzarle una secuencia no agrega control, solo agrega una
 * forma de trabarse.
 *
 * 🔴 **Desde el 02-nov ya NO esperan al ingreso.** En Toallitas (13-set) se desbloqueaban recién
 * al marcar la entrada, por pedido del usuario: *«marca su entrada y se desbloquean los botones»*.
 * En Ladysoft el pedido cambió: *«que se pueda llenar fuera de turno […] con la opción fecha para
 * poner la fecha que sea necesario»*. El formulario ahora elige el día, y un candado atado al
 * ingreso de HOY impedía justo eso: cargar lo de ayer sin turno hoy, o antes de marcar.
 * Lo que sigue amarrando cada puesto a su mercado es el turno de ESE día, que el formulario pide.
 */
function BotonesVisitaPuesto({ rotulos }: {
  /** Los nombres de cada formulario. Ladysoft (28-oct) los distingue: mayorista y cliente final. */
  rotulos: RotulosVisitasPuesto;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Link
        to="/movil/visita-puesto/comerciante"
        className="block w-full rounded-xl bg-emerald-700 py-5 text-center text-lg font-bold text-white"
      >
        {rotulos.comerciante}
      </Link>
      <Link
        to="/movil/visita-puesto/venta"
        className="block w-full rounded-xl bg-emerald-600 py-5 text-center text-lg font-bold text-white"
      >
        {rotulos.venta}
        {rotulos.ayudaVenta !== null && (
          <span className="mt-1 block px-3 text-sm font-normal">{rotulos.ayudaVenta}</span>
        )}
      </Link>
    </div>
  );
}

/**
 * Lo que la cola local aporta a esta pantalla, sin poder tumbarla.
 *
 * Nunca lanza: si la base local no se puede leer, devuelve vacío y el motivo. Mostrar el turno
 * sin el detalle de lo que está en cola es infinitamente mejor que no mostrar nada — y el motivo
 * se le dice, para que no crea que sus marcados pendientes se perdieron.
 */
async function leerColaLocal(): Promise<{
  queued: QueuedAttendance[];
  total: number;
  failed: QueuedAttendance[];
  error: string | null;
  /** El fallo es «corro código viejo»: reintentar no puede funcionar, hay que recargar. */
  versionVieja: boolean;
}> {
  try {
    const [queued, total, failed] = await Promise.all([
      listPending(), pendingCount(), listFailed(),
    ]);
    return { queued, total, failed, error: null, versionVieja: false };
  } catch (e) {
    return {
      queued: [],
      total: 0,
      failed: [],
      error: e instanceof Error ? e.message : 'No se pudo leer lo que está esperando señal.',
      versionVieja: esErrorDeVersionVieja(e),
    };
  }
}

export default function MiTurno() {
  const navigate = useNavigate();
  const { roles } = useAuth();
  // El turno de ensayo es solo para oficina: un turno ficticio en el historial de un recurso de
  // campo ensuciaría justo lo que sirve para reclamar un pago. El servidor lo vuelve a comprobar.
  const esOficina = roles.some((r) => r === 'admin' || r === 'coordinador' || r === 'comercial');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avisoCola, setAvisoCola] = useState<string | null>(null);
  const [colaVersionVieja, setColaVersionVieja] = useState(false);
  const [shifts, setShifts] = useState<MyShiftRow[]>([]);
  const [next, setNext] = useState<MyShiftRow | null>(null);
  const [marksByShift, setMarksByShift] = useState<Record<string, ShiftMark[]>>({});
  /**
   * La lectura de las marcas del servidor falló. NO es un error de pantalla entera: el turno se
   * conoce, lo que no se sabe es qué ya marcó. Antes caía en el `catch` general, el turno se
   * dibujaba «Sin marcar» y la salida quedaba apagada (ver la tarjeta).
   */
  const [marcasSinLeer, setMarcasSinLeer] = useState(false);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState<QueuedAttendance[]>([]);
  /**
   * Qué dinámicas tiene la campaña en la que está hoy. Decide qué botones se muestran.
   *
   * Se mira el turno de hoy y, si no hay, el más reciente de los últimos días: quien sube de noche
   * —o al día siguiente— ya no tiene turno «hoy», y dejarla sin botones sería quitarle el único
   * camino para registrar lo del día que sí trabajó.
   */
  const [dinamicas, setDinamicas] = useState<DinamicasDeCampania>({ canje: false, impulso: false, visitasPuesto: false });
  const [rotulosPuesto, setRotulosPuesto] = useState<RotulosVisitasPuesto>(ROTULOS_DE_SIEMPRE);
  const [creandoPrueba, setCreandoPrueba] = useState(false);
  const [errorPrueba, setErrorPrueba] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAvisoCola(null);
    setColaVersionVieja(false);
    setMarcasSinLeer(false);
    try {
      const today = todayInLima();
      // La cola local se lee APARTE y sin poder lanzar. Antes iba en el mismo `Promise.all` que
      // los turnos, y eso costó una pantalla colgada en "Cargando tus turnos…": si IndexedDB no
      // resuelve —por ejemplo, porque una actualización de esquema quedó bloqueada por otra
      // pestaña— el `Promise.all` no resuelve, el `finally` no corre y `loading` se queda en
      // true para siempre. Su turno y su botón de marcar no pueden depender de eso.
      const [shiftRows, cola] = await Promise.all([getMyShifts(today), leerColaLocal()]);
      const { queued } = cola;
      setShifts(shiftRows);
      /**
       * Los botones salen de TODAS las campañas que la persona trabaja hoy, no de una.
       *
       * Antes se tomaba `shiftRows[0]`, o sea la campaña del turno que empieza más temprano, y
       * esa decidía por las demás. Quien tenía un turno de canje a las 09:00 y uno de Vital a
       * las 11:00 se quedaba sin el botón de cierre y sin forma de reportar su jornada de Vital.
       * Son campañas independientes: se ofrece lo de cada una y la pantalla de destino ya
       * pregunta de qué turno se trata.
       */
      void dinamicasDeLasCampanias(shiftRows.map((s) => s.position.campaign_id))
        .then(setDinamicas)
        .catch(() => undefined);
      void rotulosVisitasPuesto(shiftRows.map((s) => s.position.campaign_id)).then(setRotulosPuesto);
      setPending(cola.total);
      setFailed(cola.failed);
      setAvisoCola(cola.error);
      setColaVersionVieja(cola.versionVieja);

      if (shiftRows.length > 0) {
        setNext(null);
        let server: Awaited<ReturnType<typeof listMyAttendance>> = [];
        try {
          server = await listMyAttendance(today);
        } catch {
          setMarcasSinLeer(true);
        }
        const byShift: Record<string, ShiftMark[]> = {};
        for (const s of shiftRows) {
          byShift[s.id] = [
            ...server
              .filter((a) => a.assignment_id === s.id)
              .map<ShiftMark>((a) => ({ kind: a.kind, deviceTs: a.device_ts, pending: false })),
            ...queued
              .filter((p) => p.assignmentId === s.id)
              .map<ShiftMark>((p) => ({ kind: p.kind, deviceTs: p.deviceTs, pending: true })),
          ];
        }
        setMarksByShift(byShift);
      } else {
        setMarksByShift({});
        const siguiente = await getNextShift(today);
        setNext(siguiente);
        /**
         * Sin turno hoy, se decide con el turno más reciente de los últimos días y, si tampoco hay,
         * con el próximo. Quien sube de noche o al día siguiente ya no tiene turno «hoy»: dejarla
         * sin botones le quitaría el único camino para registrar lo del día que sí trabajó.
         */
        const recientes = await getMyShiftsRange(
          formatInTimeZone(new Date(Date.now() - 4 * 86_400_000), 'America/Lima', 'yyyy-MM-dd'),
          today,
        );
        /* Mismo criterio sin turno hoy: todas las de los últimos días, más la del próximo.
           Quien sube lo de ayer de noche tiene que encontrar el botón de ayer. */
        const refs = recientes.map((r) => r.position.campaign_id);
        if (siguiente) refs.push(siguiente.position.campaign_id);
        void dinamicasDeLasCampanias(refs).then(setDinamicas).catch(() => undefined);
        void rotulosVisitasPuesto(refs).then(setRotulosPuesto);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar tus turnos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // App.tsx (Task 29) dispara 'queue-flushed' cuando la cola termina de subir.
  useEffect(() => {
    const onFlushed = () => {
      void load();
    };
    window.addEventListener('queue-flushed', onFlushed);
    return () => window.removeEventListener('queue-flushed', onFlushed);
  }, [load]);

  async function generarPrueba(): Promise<void> {
    setErrorPrueba(null);
    setCreandoPrueba(true);
    try {
      await crearTurnoDePrueba();
      await load();
    } catch (e) {
      setErrorPrueba(e instanceof Error ? e.message : 'No se pudo crear el turno de prueba.');
    } finally {
      setCreandoPrueba(false);
    }
  }

  const onMarcar = useCallback(
    (assignmentId: string, kind: 'ingreso' | 'salida') => {
      navigate(`/movil/marcar?kind=${kind}&assignment=${assignmentId}`);
    },
    [navigate],
  );

  const onDescartar = useCallback(
    async (clientUuid: string) => {
      await discardPending(clientUuid);
      await load();
    },
    [load],
  );

  if (loading) {
    return <p className="p-4 text-center text-gray-500">Cargando tus turnos…</p>;
  }

  return (
    <div className="space-y-4">
      {pending > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {pending === 1
            ? 'Tienes 1 marcado pendiente de subir. Se enviará solo al recuperar señal.'
            : `Tienes ${pending} marcados pendientes de subir. Se enviarán solos al recuperar señal.`}
        </div>
      )}

      {failed.map((f) => (
        <div
          key={f.clientUuid}
          className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800"
        >
          <p className="font-medium">
            No pudimos registrar tu {f.kind} del {fechaHora(f.deviceTs)}.
          </p>
          <p className="mt-1">
            El servidor lo rechazó varias veces. Suele pasar cuando el turno fue reasignado o
            cancelado mientras no tenías señal. Avisa a tu coordinador y pide una autorización si
            corresponde.
          </p>
          <button
            onClick={() => void onDescartar(f.clientUuid)}
            className="mt-2 font-medium underline"
          >
            Descartar este marcado
          </button>
        </div>
      ))}

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}{' '}
          <button onClick={() => void load()} className="font-medium underline">
            Reintentar
          </button>
        </div>
      )}

      {marcasSinLeer && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          No pudimos leer las marcas que ya hiciste hoy: la conexión está lenta. Si ya marcaste tu
          ingreso, marca tu salida igual; lo que ya estaba marcado no se duplica.{' '}
          <button onClick={() => void load()} className="font-medium underline">
            Volver a leer mis marcas
          </button>
        </div>
      )}

      {/* La cola local falló, pero el turno sí se pudo mostrar. Se avisa en ámbar y no en rojo:
          lo que está guardado en el celular NO se perdió, solo no se pudo leer ahora. */}
      {avisoCola && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          No se pudo leer lo que está esperando señal en este celular. Lo guardado no se perdió.
          {' '}{avisoCola}{' '}
          {/*
            🔴 La acción que se ofrece TIENE que ser la que arregla el problema.

            Cuando la pestaña corre código viejo contra una base ya actualizada, «Reintentar»
            vuelve a pedir la misma versión vieja y vuelve a fallar, siempre. Un botón que no
            puede funcionar es peor que ninguno: la persona lo aprieta tres veces, concluye que
            la app está rota y llama por teléfono. Ahí lo único que sirve es cargar el código
            nuevo.
          */}
          {colaVersionVieja ? (
            <button
              onClick={() => window.location.reload()}
              className="font-medium underline"
            >
              Recargar la app
            </button>
          ) : (
            <button onClick={() => void load()} className="font-medium underline">
              Reintentar
            </button>
          )}
        </div>
      )}

      {shifts.length > 0 ? (
        <>
          <h1 className="text-lg font-bold text-gray-900">
            {shifts.length === 1 ? 'Tu turno de hoy' : `Tus ${shifts.length} turnos de hoy`}
          </h1>
          {shifts.map((s, i) => (
            <TarjetaTurno
              key={s.id}
              shift={s}
              marks={marksByShift[s.id] ?? []}
              indice={i}
              total={shifts.length}
              onMarcar={onMarcar}
              marcasSinLeer={marcasSinLeer}
            />
          ))}
        </>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white p-4 text-center shadow-sm">
          <p className="text-base font-medium text-gray-900">No tienes turno programado para hoy.</p>
          {next ? (
            <p className="mt-2 text-sm text-gray-600">
              Tu próximo turno es el {ddmmyyyy(next.position.date)} en {next.position.pdv.name}, de{' '}
              {hhmm(next.position.start_time)} a {hhmm(next.position.end_time)}.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-600">
              Todavía no tienes turnos aprobados próximos. Consulta con tu coordinador.
            </p>
          )}
          {/* Solo para el equipo de oficina: sin un turno no hay forma de ensayar esta pantalla,
              y es la que más importa que funcione. El servidor rechaza a quien no sea de oficina
              y es idempotente por día, así que tocarlo dos veces no crea dos turnos. */}
          {esOficina && (
            <div className="mt-4 border-t pt-3">
              <button
                type="button"
                disabled={creandoPrueba}
                onClick={() => void generarPrueba()}
                className="text-sm text-blue-700 underline disabled:opacity-50"
              >
                {creandoPrueba ? 'Creando…' : 'Crear un turno de prueba para hoy'}
              </button>
              <p className="mt-1 text-xs text-gray-500">
                Turno de ensayo en la tienda de demostración. No aparece en el tablero de Hoy ni
                en Planificación.
              </p>
              {errorPrueba && <p className="mt-1 text-xs text-red-600">{errorPrueba}</p>}
            </div>
          )}
        </section>
      )}

      {/* "Fuera de turno" es no tener turno hoy, o tenerlo y haberlo cerrado ya. Los dos casos
          son el mismo para quien va a registrar: el turno terminó y esto se sube a destiempo. */}
      {/*
        Cada botón sale SOLO si la campaña tiene esa dinámica.
        Antes los dos eran fijos, y eso ofrecía «Registrar canje» a una impulsadora de Bimbo —que
        es impulso puro y no tiene canje que registrar— y «Cierre de turno» a una de Makro, que no
        tiene nada que reportar ahí. Reportado por el usuario: «para la campaña de Bimbo solamente
        tiene que salir ingreso, salida y registrar cierre de turno».
      */}
      {dinamicas.canje && (
        <BotonCanjes
          fueraDeTurno={
            shifts.length === 0 ||
            shifts.every((s) => computeShiftState(marksByShift[s.id] ?? []).hasSalida)
          }
        />
      )}

      {dinamicas.impulso && <BotonCierreDeTurno />}

      {dinamicas.visitasPuesto && <BotonesVisitaPuesto rotulos={rotulosPuesto} />}

      <div className="flex flex-col gap-2 pt-2">
        <Link
          to="/movil/solicitud"
          className="rounded-lg border border-blue-600 py-3 text-center text-sm font-medium text-blue-700"
        >
          Solicitar autorización
        </Link>
        <Link to="/movil/historial" className="py-2 text-center text-sm text-gray-600 underline">
          Ver mi historial
        </Link>
      </div>

      {/*
        BANCO DE PRUEBAS del equipo de oficina. Pedido del usuario el 2026-09-05: quería, en su
        usuario y en el de Roberto, la misma puerta que ya tenía para el turno de ensayo pero para
        **ver los formularios**, incluido el del supervisor.

        Va acá abajo y SIEMPRE visible para oficina, no dentro del bloque de «no tienes turno»: ese
        bloque desaparece justo después de crear el turno de prueba, o sea que la puerta se cerraba
        sola en el momento en que empezaba a servir.

        El enlace a «Mi ruta» no da ningún permiso nuevo —la pestaña ya está en el menú de quien
        tiene rol de oficina y el servidor decide igual—: lo que hace es que se encuentre desde
        donde el usuario ya sabe buscar.
      */}
      {esOficina && (
        <section className="mt-2 rounded-xl border border-dashed border-gray-300 p-3">
          <p className="text-xs font-semibold text-gray-700">Para revisar los formularios</p>
          <div className="mt-2 flex flex-col gap-1">
            <Link to="/movil/mi-ruta" className="text-sm text-blue-700 underline">
              Mi ruta — la pantalla del supervisor
            </Link>
            {!dinamicas.impulso && (
              <Link to="/movil/cierre-de-turno" className="text-sm text-blue-700 underline">
                Cierre de turno — el formulario de impulso
              </Link>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Nada de lo que registres acá entra a la planilla: la ruta del supervisor no es
            asistencia, y el turno de ensayo vive en la campaña de demostración.
          </p>
        </section>
      )}
    </div>
  );
}
